import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../../lib/api';
const API_URL = API_BASE;
import { Calendar, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';

const hdrs = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function MaturedPDCScreen({ mode = 'receipt', theme }) {
  const isDark = theme === 'dark';
  const title = `Matured PDC Vouchers (${mode === 'payment' ? 'Payments' : 'Receipts'})`;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [processing, setProcessing] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const loadRecords = useCallback(async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const r = await fetch(`${API_URL}/api/vouchers/matured-pdc?type=${mode}`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) { setRecords(j.data || []); setSelected(new Set()); }
      else setMessage({ type: 'error', text: j.error || 'Load failed' });
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    setLoading(false);
  }, [mode]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const process = async (docId, action) => {
    setProcessing(docId);
    setMessage({ type: '', text: '' });
    try {
      const r = await fetch(`${API_URL}/api/vouchers/matured-pdc/${docId}/process`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ action, type: mode }),
      });
      const j = await r.json();
      if (j.success) { setMessage({ type: 'success', text: j.message }); loadRecords(); }
      else setMessage({ type: 'error', text: j.error || 'Process failed' });
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    setProcessing(null);
  };

  const toggleSelect = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () => setSelected(prev => prev.size === records.length ? new Set() : new Set(records.map(r => r.DocId)));

  const processSelected = async (action) => {
    for (const docId of selected) await process(docId, action);
  };

  const base = isDark ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900';
  const card = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const inp = isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300';
  const thC = isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600';

  return (
    <div className={`min-h-screen p-4 ${base}`} data-testid="matured-pdc-screen">
      {/* Header */}
      <div className={`border rounded-lg p-4 mb-4 ${card}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-orange-500" />
            <h2 className="font-semibold text-lg">{title}</h2>
            {records.length > 0 && (
              <span className="ml-2 bg-orange-100 text-orange-700 text-xs font-medium px-2 py-0.5 rounded-full">{records.length} matured</span>
            )}
          </div>
          <button onClick={loadRecords} disabled={loading} className="erp-btn erp-btn-ghost flex items-center gap-1" data-testid="matured-pdc-refresh-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Shows all PDC vouchers where the cheque date has passed (matured) and status is still Pending.
        </p>
      </div>

      {/* Bulk action toolbar */}
      {selected.size > 0 && (
        <div className={`border rounded-lg p-3 mb-3 flex items-center gap-3 ${isDark ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
          <span className="text-sm font-medium">{selected.size} selected</span>
          <button onClick={() => processSelected('Deposited')} className="erp-btn erp-btn-primary text-xs flex items-center gap-1" data-testid="matured-pdc-bulk-deposit">
            <CheckCircle size={12} /> Mark Deposited
          </button>
          <button onClick={() => processSelected('Bounced')} className="erp-btn erp-btn-danger text-xs flex items-center gap-1" data-testid="matured-pdc-bulk-bounce">
            <XCircle size={12} /> Mark Bounced
          </button>
          <button onClick={() => processSelected('Cleared')} className="erp-btn erp-btn-ghost text-xs" data-testid="matured-pdc-bulk-clear">Mark Cleared</button>
        </div>
      )}

      {/* Message */}
      {message.text && (
        <div className={`flex items-center gap-2 p-3 rounded mb-3 text-sm ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-testid="matured-pdc-message">
          {message.type === 'error' ? <AlertCircle size={14} /> : <CheckCircle size={14} />} {message.text}
        </div>
      )}

      {/* Grid */}
      <div className={`border rounded-lg overflow-hidden ${card}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={thC}>
              <th className="p-2 w-8">
                <input type="checkbox" checked={selected.size === records.length && records.length > 0} onChange={toggleAll} className="cursor-pointer" />
              </th>
              <th className="text-left p-2 font-medium">Doc ID</th>
              <th className="text-left p-2 font-medium">Voucher Date</th>
              <th className="text-left p-2 font-medium">Account</th>
              <th className="text-right p-2 font-medium">Amount</th>
              <th className="text-left p-2 font-medium">Cheque No</th>
              <th className="text-left p-2 font-medium">PDC / Cheque Date</th>
              <th className="text-left p-2 font-medium">Narration</th>
              <th className="text-center p-2 font-medium">Process</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={r.DocId} className={`border-t ${isDark ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-100 hover:bg-gray-50'} ${selected.has(r.DocId) ? (isDark ? 'bg-blue-900/20' : 'bg-blue-50') : ''}`}>
                <td className="p-2 text-center">
                  <input type="checkbox" checked={selected.has(r.DocId)} onChange={() => toggleSelect(r.DocId)} className="cursor-pointer" data-testid={`matured-pdc-check-${i}`} />
                </td>
                <td className="p-2 font-mono text-xs">{r.DocId}</td>
                <td className="p-2">{r.DocDateFormatted || r.DocDate}</td>
                <td className="p-2">{r.AccountName}</td>
                <td className="p-2 text-right font-medium">{parseFloat(r.Amount || 0).toFixed(2)}</td>
                <td className="p-2">{r.Chequeno}</td>
                <td className="p-2 font-medium text-orange-600">{r.ChequeDateFormatted || (r.ChequeDate ? new Date(r.ChequeDate).toISOString().split('T')[0] : '')}</td>
                <td className="p-2 text-xs text-gray-500 max-w-xs truncate">{r.CommNarration}</td>
                <td className="p-2">
                  <div className="flex gap-1 justify-center">
                    <button onClick={() => process(r.DocId, 'Deposited')} disabled={processing === r.DocId} className="erp-btn erp-btn-primary text-xs px-2 py-1" data-testid={`matured-pdc-deposit-${i}`}>
                      {processing === r.DocId ? '…' : 'Deposit'}
                    </button>
                    <button onClick={() => process(r.DocId, 'Bounced')} disabled={processing === r.DocId} className="erp-btn erp-btn-danger text-xs px-2 py-1" data-testid={`matured-pdc-bounce-${i}`}>
                      Bounce
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {records.length === 0 && !loading && (
          <div className={`p-8 text-center text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            No matured PDC vouchers pending — all cheques are accounted for
          </div>
        )}
      </div>
    </div>
  );
}
